<template>
  <div v-if="route.meta.public" class="login-page-wrapper">
    <router-view />
  </div>

  <router-view v-else-if="route.meta.area === 'portal'" />

  <div v-else id="app-layout">
    <aside class="sidebar">
      <div class="sidebar-logo">
        <img src="./assets/solvea-logo-white.png" alt="Solvea" class="logo-img" />
      </div>
      <nav class="sidebar-nav">
        <router-link
          v-for="item in visibleNavItems"
          :key="item.to"
          :to="item.to"
          class="nav-item"
        >
          <span class="nav-icon">{{ item.icon }}</span>
          {{ item.label }}
        </router-link>
      </nav>

      <div class="sidebar-user-footer">
        <div class="sidebar-user-info">
          <UserRound class="user-avatar" :size="18" :stroke-width="1.75" />
          <div class="user-details">
            <span class="user-email">{{ userEmail }}</span>
            <span class="user-role-badge">{{ roleLabel }}</span>
          </div>
        </div>
        <button class="sidebar-logout-btn" title="Abmelden" @click="handleLogout">
          <LogOut :size="18" :stroke-width="1.75" />
        </button>
      </div>
    </aside>
    <main class="main-content">
      <router-view />
    </main>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { UserRound, LogOut } from 'lucide-vue-next'
import { logout, currentUser } from '@/services/auth'
import { useDeploymentMode } from '@/composables/useDeploymentMode'

const { deployment } = useDeploymentMode()

const router = useRouter()
const route  = useRoute()

const userEmail = computed(() => currentUser.value?.email || '')
const userRole  = computed(() => currentUser.value?.role || null)
const roleLabel = computed(() => ({
  admin:             'Admin',
  portfolio_manager: 'Portfolio Manager',
  analyst:           'Analyst',
}[userRole.value] || userRole.value))

// Mirrors router/index.js's meta.roles/meta.page checks exactly, so the
// menu and the actual route guard can never drift apart (Teil 4) - an item
// only appears here if navigating to it would not immediately bounce to
// /forbidden.
const NAV_ITEMS = [
  // localOnly (Portal-Administration auf dem VPS): entspricht genau den
  // Routen in router/index.js mit meta.localOnly - auf dem VPS
  // (deployment === 'portal') ausgeblendet, da die zugehörigen Backend-
  // Routen dort serverseitig 404 liefern (app.py, MERIDIAN_DEPLOYMENT).
  { to: '/expectations-framework', label: 'Expectations Framework', icon: '▲', page: 'analyze', localOnly: true },
  { to: '/upcoming-events',        label: 'Upcoming Events',        icon: '📅', page: 'events', localOnly: true },
  { to: '/watchlist',              label: 'Watchlist',              icon: '★', page: 'watchlist', localOnly: true },
  { to: '/logs',                   label: 'Logs',                   icon: '📋' },
  { to: '/control',                label: 'Control Center',         icon: '⚙',  page: 'control_center', roles: ['admin', 'portfolio_manager'], localOnly: true },
  { to: '/user-management',        label: 'User Management',        icon: '👥', page: 'user_management', roles: ['admin'] },
  // 'roles' is what actually narrows this to admin - there is no real
  // 'report_admin' page_key (see router/index.js comment on this route),
  // so `page` is omitted here rather than checked against something that
  // doesn't exist. The Sicherheits-Audit is explicit that this entry (like
  // Control Center/User Management) is convenience only; the enforced
  // boundary is roles=['admin'] on the backend endpoints themselves.
  { to: '/admin/reports',          label: 'Reports',                 icon: '📄', roles: ['admin'] },
  // Universe (Refinitiv-Stammdaten, refdata): nur Admin - Backend /api/universe* prueft @require_auth(["admin"]); localOnly wie die
  // Route (im Portal-Modus serverseitig 404).
  { to: '/admin/universe',         label: 'Universe',                icon: '🌐', roles: ['admin'], localOnly: true },
]

const visibleNavItems = computed(() => {
  const me = currentUser.value
  if (!me) return []
  return NAV_ITEMS.filter((item) => {
    if (item.roles && me.role !== 'admin' && !item.roles.includes(me.role)) return false
    if (item.page && me.role !== 'admin' && !(me.page_keys || []).includes(item.page)) return false
    if (item.localOnly && deployment.value === 'portal') return false
    return true
  })
})

async function handleLogout() {
  await logout()
  router.push('/login')
}
</script>

<style>
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: 'Segoe UI', Arial, sans-serif;
  background: #ffffff;
  color: #1a1a2e;
}

#app-layout {
  display: flex;
  min-height: 100vh;
}

.sidebar {
  width: 240px;
  min-height: 100vh;
  background-color: #003366;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
}

.sidebar-logo {
  padding: 8px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  display: flex;
  justify-content: center;
}

.logo-img {
  width: 160px;
  max-width: 100%;
  height: auto;
  display: block;
  filter: brightness(0) invert(1);
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  padding: 16px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 24px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
  border-left: 3px solid transparent;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.nav-item.router-link-active {
  background-color: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  border-left-color: #4da6ff;
}

.nav-icon {
  font-size: 14px;
  opacity: 0.85;
}

.main-content {
  margin-left: 240px;
  flex: 1;
  padding: 40px 48px;
  background: #ffffff;
  min-height: 100vh;
}

.view-container h1 {
  font-size: 28px;
  font-weight: 700;
  color: #003366;
  margin-bottom: 8px;
}

.view-container .subtitle {
  font-size: 15px;
  color: #6b7280;
}

/* ── User Footer ──────────────────────────────────────────────────────────── */
.sidebar-user-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
}

.sidebar-user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.user-avatar {
  color: rgba(255, 255, 255, 0.75);
  flex-shrink: 0;
}

.user-details {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.user-email {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
}

.user-role-badge {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sidebar-logout-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  font-size: 18px;
  padding: 4px;
  border-radius: 4px;
  flex-shrink: 0;
  transition: color 0.15s, background 0.15s;
  line-height: 1;
}

.sidebar-logout-btn:hover {
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.12);
}
</style>
